<script setup lang="ts">
/**
 * `<Icon>` 统一图标入口（前台 Icon Component 约定）。
 *
 * - 数据源：`src/assets/icons/*.svg`，通过 Vite glob `?raw` 内联为
 *   字符串模板，构建期就把 SVG 打入产物，运行时不发额外请求。
 * - 未注册 name → 渲染兜底圆角方块占位 + console.warn，不抛错；
 *   业务组件**禁止**直接 inline SVG，新增图标 = 加 SVG 文件即可。
 * - 颜色：SVG 内部 `stroke="currentColor"`，调用方通过 CSS `color`
 *   控制描边色（如 `color: var(--color-brand-primary)`）。
 */
import { computed, defineComponent, h } from 'vue';

import { iconRegistry, isRegisteredIcon } from './icon-registry';

const props = withDefaults(
  defineProps<{
    name: string;
    size?: number | string;
    title?: string;
  }>(),
  {
    size: 20,
    title: undefined,
  },
);

const sizeValue = computed(() =>
  typeof props.size === 'number' ? `${props.size}px` : props.size,
);

const svgMarkup = computed<string | null>(() => {
  if (isRegisteredIcon(props.name)) {
    return iconRegistry[props.name] ?? null;
  }
  // 显式 console.warn：未知 name 暴露而非静默回退
  // eslint-disable-next-line no-console
  console.warn(`[Icon] unknown icon name="${props.name}"`);
  return null;
});

const TrustedSvg = defineComponent({
  name: 'TrustedSvg',
  props: {
    markup: {
      type: String,
      required: true,
    },
    className: {
      type: String,
      required: true,
    },
  },
  setup(iconProps) {
    return () =>
      h('span', {
        class: iconProps.className,
        'data-testid': 'icon-svg',
        innerHTML: iconProps.markup,
      });
  },
});
</script>

<template>
  <span
    :class="$style.icon"
    :style="{ width: sizeValue, height: sizeValue }"
    :role="title ? 'img' : 'presentation'"
    :aria-label="title"
  >
    <TrustedSvg
      v-if="svgMarkup"
      :class-name="$style.svg"
      :markup="svgMarkup"
    />
    <span
      v-else
      :class="$style.fallback"
      data-testid="icon-fallback"
      aria-hidden="true"
    ></span>
  </span>
</template>

<style module>
.icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  color: inherit;
  line-height: 0;
}

.svg {
  display: inline-flex;
  width: 100%;
  height: 100%;
}

.svg :global(svg) {
  width: 100%;
  height: 100%;
  display: block;
}

.fallback {
  display: inline-block;
  width: 100%;
  height: 100%;
  background: var(--color-bg-secondary);
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-sm);
}
</style>
